import { fillSelection, personaById, type Persona } from '@strategy4kids/persona';
import { usePersonaStore } from './persona-store.js';

/** The seat order the Rummy / Crazy Eights / Colour Dash / Poker / Blackjack tables fill their cast in. */
export const CARD_TABLE_SEATS: readonly string[] = ['S', 'W', 'N', 'E'];

/**
 * Which character sits where at a card table (the pure form of `useSeatPersonas`, so a store can
 * ask without a hook). The one the player picked on Home sits across (the partner seat); the others
 * are filled with distinct characters in roster order, so a table always has different faces and
 * the same table looks the same every time. `seats` is the table's seat order (it decides who gets
 * which filler character, so it must match what the screen passes to the hook).
 */
export function personaMapForSeats(
  seats: readonly string[],
  pickedId: string,
  partnerSeat = 'N',
): Record<string, Persona | undefined> {
  const selection: Record<string, string | undefined> = {};
  for (const seat of seats) selection[seat] = seat === partnerSeat ? pickedId : undefined;
  const filled = fillSelection(selection);
  const out: Record<string, Persona | undefined> = {};
  for (const seat of seats) {
    const id = filled[seat];
    out[seat] = id ? personaById(id) : undefined;
  }
  return out;
}

/** The persona id at `seat`, read from the persona store now (no hook), or undefined for an unknown seat. */
export function personaForSeat(
  seat: string,
  seats: readonly string[] = CARD_TABLE_SEATS,
  partnerSeat = 'N',
): string | undefined {
  return personaMapForSeats(seats, usePersonaStore.getState().personaId, partnerSeat)[seat]?.id;
}

/** The hook form for screens: re-renders when the picked character changes. */
export function useSeatPersonas(seats: readonly string[], partnerSeat = 'N'): Record<string, Persona | undefined> {
  const picked = usePersonaStore((s) => s.personaId);
  return personaMapForSeats(seats, picked, partnerSeat);
}
